// src/pages/ScriptManagement.jsx
import React, { useState, useEffect, useContext } from 'react';

import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Alert, Chip, Grid, FormControl, InputLabel, Select, MenuItem,
  Avatar, Tooltip, Stack, Card, CardContent, Tabs, Tab, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  Code as CodeIcon, Refresh as RefreshIcon, Close as CloseIcon, Info as InfoIcon,
  Webhook as WebhookIcon, Description as DescriptionIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import API_URLS from '../config/api';
import { axiosConfig } from '../utils/csrf';

const ScriptManagement = () => {
  const { user } = useContext(AuthContext);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [editingScript, setEditingScript] = useState(null);
  const [formData, setFormData] = useState({
    script_name: '',
    script_slug: '',
    description: '',
    protocol: 'tradingview',
    webhook_endpoint: '',
    webhook_secret: '',
    is_active: true
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchScripts();
    }
  }, [user]);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URLS.SCRIPTS_LIST, axiosConfig());
      setScripts(response.data.scripts || []);
      setError(null);
    } catch (err) {
      setError('Failed to load scripts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (script) => {
    setSelectedScript(script);
    setDetailTab(0);
    setOpenDetailDialog(true);
  };

  const handleOpenDialog = (script = null) => {
    if (script) {
      setEditingScript(script);
      setFormData({
        script_name: script.script_name,
        script_slug: script.script_slug,
        description: script.description || '',
        protocol: script.protocol || 'tradingview',
        webhook_endpoint: script.webhook_endpoint,
        webhook_secret: script.webhook_secret || '',
        is_active: script.is_active
      });
    } else {
      setEditingScript(null);
      setFormData({
        script_name: '',
        script_slug: '',
        description: '',
        protocol: 'tradingview',
        webhook_endpoint: '',
        webhook_secret: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingScript(null);
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));

    if (name === 'script_name') {
      const slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, script_slug: slug, webhook_endpoint: slug }));
    }
  };

  const generateSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, webhook_secret: secret }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (editingScript) {
        await axios.put(API_URLS.SCRIPT_DETAIL(editingScript.id), formData, axiosConfig());
        setSuccess('Script updated successfully');
      } else {
        await axios.post(API_URLS.SCRIPT_CREATE, formData, axiosConfig());
        setSuccess('Script created successfully');
      }
      handleCloseDialog();
      fetchScripts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save script');
    }
  };

  const handleDelete = async (scriptId) => {
    if (!window.confirm('Delete this script? This will remove all associated data.')) return;

    try {
      await axios.delete(API_URLS.SCRIPT_DETAIL(scriptId), axiosConfig());
      setSuccess('Script deleted successfully');
      fetchScripts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete script');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setSuccess(`${label} copied to clipboard`);
  };

  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
            Trading Scripts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure TradingView webhook endpoints and manage your trading scripts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          Add Script
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>{success}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
              <TableCell sx={{ fontWeight: 700 }}>Script Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Webhook Endpoint</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Protocol</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <RefreshIcon sx={{ animation: 'spin 1s linear infinite', fontSize: 40, color: '#667eea' }} />
                  <Typography color="text.secondary" sx={{ mt: 2 }}>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : scripts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <CodeIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>No scripts yet</Typography>
                  <Typography color="text.secondary">Create your first script to get started</Typography>
                </TableCell>
              </TableRow>
            ) : (
              scripts.map((script) => (
                <TableRow 
                  key={script.id}
                  onClick={() => handleViewDetails(script)}
                  sx={{ 
                    '&:hover': { background: '#f8fafc', cursor: 'pointer' },
                    transition: 'background 0.2s',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {script.script_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        /api/webhook/tradingview/
                      </Typography>
                      <Tooltip title="Copy webhook URL">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(`${window.location.origin}/api/webhook/tradingview/`, 'Webhook URL');
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {script.protocol || 'TradingView'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={script.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        background: script.is_active ? '#dcfce7' : '#fee2e2',
                        color: script.is_active ? '#166534' : '#991b1b',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(script.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(script)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(script.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Script Details Dialog */}
      <Dialog 
        open={openDetailDialog} 
        onClose={() => setOpenDetailDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={700}>
              {selectedScript?.script_name}
            </Typography>
            <IconButton onClick={() => setOpenDetailDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Documentation" icon={<DescriptionIcon />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Webhook" icon={<WebhookIcon />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {detailTab === 0 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedScript?.description || 'No description'}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Script Slug</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selectedScript?.script_slug}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedScript?.is_active ? 'Active' : 'Inactive'} 
                    size="small"
                    sx={{ mt: 1, fontWeight: 700 }}
                  />
                </Box>
              </Stack>
            )}

            {detailTab === 1 && (
              <Stack spacing={3}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    How to Use This Script
                  </Typography>
                  <Typography variant="body2">
                    Configure your TradingView alert to send a webhook with the script name in the JSON payload.
                  </Typography>
                </Alert>

                <Card sx={{ background: '#f8fafc', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} mb={2}>
                      TradingView Alert Message Format
                    </Typography>
                    <Box sx={{ background: '#1e293b', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', color: '#e2e8f0' }}>
                      {`{\n  "script_name": "${selectedScript?.script_name}",\n  "signal": "{{plot_0}}",\n  "ticker": "{{ticker}}",\n  "price": "{{close}}"\n}`}
                    </Box>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(`{\n  "script_name": "${selectedScript?.script_name}",\n  "signal": "{{plot_0}}",\n  "ticker": "{{ticker}}",\n  "price": "{{close}}"\n}`, 'JSON template')}
                      sx={{ mt: 2, textTransform: 'none' }}
                    >
                      Copy Template
                    </Button>
                  </CardContent>
                </Card>
              </Stack>
            )}

            {detailTab === 2 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Webhook URL</Typography>
                  <Box sx={{ background: '#f8fafc', p: 2, borderRadius: 2, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {`${window.location.origin}/api/webhook/tradingview/`}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(`${window.location.origin}/api/webhook/tradingview/`, 'Webhook URL')}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Copy URL
                  </Button>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Required Headers</Typography>
                  <Box sx={{ background: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      Content-Type: application/json
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h5" fontWeight={700}>
              {editingScript ? 'Edit Script' : 'Create New Script'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Script Name"
                  name="script_name"
                  value={formData.script_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Zero A - 1min"
                  helperText="This must match the script_name in your TradingView webhook"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Brief description of this trading script"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Script Slug"
                  name="script_slug"
                  value={formData.script_slug}
                  onChange={handleChange}
                  required
                  helperText="Auto-generated from name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Protocol</InputLabel>
                  <Select
                    name="protocol"
                    value={formData.protocol}
                    onChange={handleChange}
                    label="Protocol"
                  >
                    <MenuItem value="tradingview">TradingView</MenuItem>
                    <MenuItem value="metatrader">MetaTrader</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
              }}
            >
              {editingScript ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <style>
        {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>
    </Box>
  );
};

export default ScriptManagement;
